import { useMemo } from 'react';
import type { AccommodationListingDTO, Filters } from '@shared/types/Accommodations.types';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { useAccommodationsSortOrder } from './useAccommodationsSortOrder';
import { sortByFavorite, sortByHostStayingHere, sortByOrderList } from '@shared/utils/array';
import { useListingsSortQueryParam } from './useAccommodationsConsoleSearchParamsState/useListingsSortQueryParam';
import { usePrioritizeTagsQueryParam } from './useAccommodationsConsoleSearchParamsState/usePrioritizeTagsQueryParam';
import type { LatLngLiteral } from '@shared/core/google-maps/types';
import { SortOption } from '@shared/constants/accommodations';
import { useIsAllInTotalEnabled } from '@shared/hooks/useAllInTotal/useAllInTotal';
import { getCashFundCoverage } from '@shared/utils/cashFund/cashFundCoverage';
import type { CashFundConfig } from '@shared/utils/cashFund/cashFund.types';

type Bucket = 'tagged' | 'available' | 'unpriced' | 'soldOut';

type UseListingSortArgs = Readonly<{
  defaultSortOption: SortOption;
  listings: ReadonlyArray<AccommodationListingDTO>;
  filters?: Filters;
  eventHandle?: Maybe<string>;
  sortAnchor?: Maybe<{ name: string; location: Maybe<LatLngLiteral> }>;
  /** Injected by the cash-fund scenario for the HIGHEST_FUND_SAVINGS ranking. */
  cashFund?: CashFundConfig;
  /** Stay length and room count of the current search — used to put the all-in whole-stay total back
   *  on a per-night scale for the price sorts. Both are constant across a search. */
  noOfNights?: number;
  unitCount?: number;
}>;

export const useListingSort = ({ listings, defaultSortOption, sortAnchor, filters, eventHandle, cashFund, noOfNights, unitCount }: UseListingSortArgs) => {
  const { currentSort, updateSort } = useListingsSortQueryParam(defaultSortOption);
  // Read here rather than passed in: the cards read the same hook, so neither side can end up in
  // the other's arm, and no caller has to remember to thread it through.
  const isAllInTotalEnabled = useIsAllInTotalEnabled();
  // Read here rather than passed in, for the same reason as isAllInTotalEnabled: no
  // caller has to remember to thread the inbound tag context through.
  // Same hook the search request uses, so the tags asked for and the tags sorted on cannot diverge.
  const prioritizeTags = usePrioritizeTagsQueryParam();

  const isCouplesPicksRelevant = currentSort === SortOption.COUPLES_PICKS || defaultSortOption === SortOption.COUPLES_PICKS;
  const { pageContainerBySlug } = useAccommodationsSortOrder({ eventHandle, skip: !isCouplesPicksRelevant });

  const sortedListings = useMemo(() => {
    let sorted: ReadonlyArray<AccommodationListingDTO>;

    // Price sorts follow whichever total the cards render (TRAV-4298). Under the all-in arm the card's
    // "$X total" line carries the pay-at-property charges, so ordering by the collected total would put
    // that line visibly out of order.
    //
    // The all-in figure is divided back down to a per-room, per-night number rather than compared whole.
    // Nights and rooms are constant across a search, so this leaves the ordering of the priced hotels
    // identical to ordering by the all-in total — but it keeps listings that have no whole-stay total at
    // all (room blocks, whose rate is the couple's own contract) on the same scale instead of sorting a
    // per-night number against whole-stay ones, which would sweep every room block to the front.
    const nightsDivisor = Math.max(1, noOfNights ?? 1);
    const roomsDivisor = Math.max(1, unitCount ?? 1);
    const priceSortValue = (listing: AccommodationListingDTO): number | undefined => {
      const values = listing.pricingInfo?.values;
      if (!values) return undefined;
      if (isAllInTotalEnabled && values.allInTotalRate != null) {
        return values.allInTotalRate / nightsDivisor / roomsDivisor;
      }
      return values.nightlyBaseRate;
    };

    switch (currentSort) {
      case SortOption.TOP_MATCHES:
        sorted = [...listings].sort((a, b) => (b.searchScore ?? 0) - (a.searchScore ?? 0));
        break;
      case SortOption.COUPLES_PICKS: {
        const blockOrdering = pageContainerBySlug?.blockOrdering;
        const coupleListings = listings.filter(listing => listing.isPickedByCouple);
        const nonCoupleListings = listings.filter(listing => !listing.isPickedByCouple);
        const sortedCoupleListings = sortByFavorite(sortByHostStayingHere(sortByOrderList(coupleListings, blockOrdering ?? [])));
        sorted = [...sortedCoupleListings, ...nonCoupleListings];
        break;
      }

      case SortOption.PRICE_LOW_TO_HIGH:
        sorted = [...listings].sort((a, b) => {
          const aPrice = priceSortValue(a) ?? Number.MAX_VALUE;
          const bPrice = priceSortValue(b) ?? Number.MAX_VALUE;
          return aPrice - bPrice;
        });
        break;

      case SortOption.PRICE_HIGH_TO_LOW:
        sorted = [...listings].sort((a, b) => {
          const aPrice = priceSortValue(a) ?? -1;
          const bPrice = priceSortValue(b) ?? -1;
          return bPrice - aPrice;
        });
        break;

      case SortOption.MOST_SAVINGS:
        sorted = [...listings].sort((a, b) => {
          const aDiscount = a.pricingInfo?.values?.discountPercentage ?? 0;
          const bDiscount = b.pricingInfo?.values?.discountPercentage ?? 0;
          return bDiscount - aDiscount;
        });
        break;

      case SortOption.HIGHEST_FUND_SAVINGS: {
        // Cheapest out-of-pocket first. The server's split makes this a real ranking now — before
        // partial cover every eligible stay owed $0, so this could only group.
        // Computed once per listing; the comparator then does no work.
        const outOfPocketByListing = new Map(listings.map(listing => [listing, getCashFundCoverage(listing, cashFund)?.outOfPocketMinorUnits]));
        // Ineligible listings have no split, so they sort last — the old grouping, kept as a side effect.
        const outOfPocketOf = (listing: AccommodationListingDTO) => outOfPocketByListing.get(listing) ?? Number.MAX_VALUE;
        sorted = [...listings].sort((a, b) => outOfPocketOf(a) - outOfPocketOf(b));
        break;
      }

      case SortOption.DISTANCE:
        sorted = [...listings].sort((a, b) => {
          if (sortAnchor?.location) {
            const aDistance = distanceInMiles(sortAnchor.location, a.location) ?? Number.MAX_VALUE;
            const bDistance = distanceInMiles(sortAnchor.location, b.location) ?? Number.MAX_VALUE;
            return aDistance - bDistance;
          }
          return 0;
        });
        break;

      default:
        sorted = listings;
    }

    const requestedTags = new Set(prioritizeTags);
    const isTagMatch = (listing: AccommodationListingDTO): boolean => (prioritizeTags.length ? (listing.tags?.some(tag => requestedTags.has(tag)) ?? false) : false);

    // Lift tag-matched listings (?prioritizeTag=honeymoon) to the front of whichever
    // sort the user chose. A stable partition, so the chosen ordering survives inside
    // both groups — this reorders the page, it does not re-rank it.
    if (prioritizeTags.length) {
      const matched: AccommodationListingDTO[] = [];
      const unmatched: AccommodationListingDTO[] = [];

      for (const listing of sorted) {
        if (isTagMatch(listing)) {
          matched.push(listing);
        } else {
          unmatched.push(listing);
        }
      }

      // Every listing survives — with only a few hundred tagged hotels, filtering
      // would empty most destinations.
      sorted = [...matched, ...unmatched];
    }

    // When showAvailableOnly is true, push sold-out hotels to the end (and unpriced hotels near the end)
    if (filters?.showAvailableOnly) {
      const buckets: Readonly<Record<Bucket, AccommodationListingDTO[]>> = {
        tagged: [],
        available: [],
        unpriced: [],
        soldOut: [],
      };

      for (const listing of sorted) {
        let bucket: Bucket = 'available';

        if (listing.type === 'standardHotel' && !listing.isSponsored) {
          if (listing.isSoldOutForSelectedDates) {
            bucket = 'soldOut';
          } else if (!listing.pricingInfo.isLoading && !listing.pricingInfo.values) {
            bucket = 'unpriced';
          }
        }

        /**
         * A requested tag outranks having a price, but not being sold out.
         *
         * This bucketing used to run blind to the tag and simply discarded the lift above: a
         * curated stay whose provider returned no rate fell into `unpriced`, behind every priced
         * hotel on the page — so `/honeymoon` buried the very stays it exists to lead with. That
         * was never a decision, just an interaction nobody had looked at.
         *
         * Sold-out is left alone deliberately. No rate at all means "we could not price this",
         * which is a gap in our data and no reason to hide an editorial pick; sold out means the
         * guest cannot book it, and leading with it would waste their click. Tagged sold-out stays
         * still sort first *within* the sold-out group, because the partition above ran first.
         */
        if (bucket !== 'soldOut' && isTagMatch(listing)) {
          bucket = 'tagged';
        }

        buckets[bucket].push(listing);
      }

      return [...buckets.tagged, ...buckets.available, ...buckets.unpriced, ...buckets.soldOut];
    }

    return sorted;
  }, [listings, pageContainerBySlug?.blockOrdering, currentSort, sortAnchor, filters?.showAvailableOnly, cashFund, isAllInTotalEnabled, noOfNights, unitCount, prioritizeTags]);

  return {
    sortedListings,
    sortOption: currentSort,
    updateSort,
  };
};
